import React, { useState, useEffect, useContext } from "react";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";
import { AuthContext } from "../../../Context/AuthContext";
import { Authorization } from "../../../Context/Authorization";
import Preloader from "../../../shared/PreLoaders";

const AddGO = () => {
  const initialStage = {
    documentName: "",
    documentType: "",
    documentNo: "",
    date: "",
    details: "",
    uploadDocument: "",
    circleId: "",
    divisionId: "",
    rangeId: "",
    vfcId: "",
  };
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(initialStage);
  const [circles, setCircles] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [ranges, setRanges] = useState([]);
  const [vfcs, setVfcs] = useState([]);
  const [errors, setErrors] = useState({});
  const { userId } = useContext(AuthContext);
  const Api = process.env.REACT_APP_API;

  // Fetch Circles, Divisions, Ranges, and VFCs
  useEffect(() => {
    setLoading(true);
    const fetchOptions = async () => {
      try {
        const Config = Authorization();
        const [circlesRes, divisionsRes, rangesRes, vfcsRes] =
          await Promise.all([
            axios.get(`${Api}/api/circles`, Config),
            axios.get(`${Api}/api/divisions`, Config),
            axios.get(`${Api}/api/ranges`, Config),
            axios.get(`${Api}/api/vfcs`, Config),
          ]);

        // Setting the entire response data without filtering
        setCircles(circlesRes.data);
        setDivisions(divisionsRes.data);
        setRanges(rangesRes.data);
        setVfcs(vfcsRes.data.vfcs);
      } catch (error) {
        console.error("Error fetching Data", error);
        toast.error("Error fetching Data");
      }
      setLoading(false);
    };

    fetchOptions();
  }, [Api]); // access still triggers the effect, but no filtering

  // Handle the "All" selection logic
  const handleSelectChange = (e) => {
    const { id, value } = e.target;

    if (value === "ALL") {
      const allValues = {
        circleId: circles.map((c) => c._id),
        divisionId: divisions.map((d) => d._id),
        rangeId: ranges.map((r) => r._id),
        vfcId: vfcs.map((v) => v._id),
      };
      setFormData({ ...formData, [id]: allValues[id] });
    } else {
      setFormData({ ...formData, [id]: [value] }); // Pass as an array even for a single value
    }
  };

  // Validation function
  const validateForm = (formData) => {
    const newErrors = {};
    if (!formData.documentName)
      newErrors.documentName = "Please enter Document Name";
    if (!formData.documentType)
      newErrors.documentType = "Please select Document Type";
    if (!formData.documentNo) newErrors.documentNo = "Please enter Document No";
    if (!formData.date) newErrors.date = "Please enter Date";
    if (!formData.details) newErrors.details = "Please enter Details";
    if (!formData.uploadDocument)
      newErrors.uploadDocument = "Please enter Upload Document";
    return newErrors;
  };

  const handleChange = (e) => {
    const { id, value, files } = e.target;
    setFormData({
      ...formData,
      [id]: files ? files[0] : value, // If file input, set the first file
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Please fill all details");
      return;
    }

    try {
      const Config = Authorization();
      const formDataToSend = new FormData();
      formDataToSend.append("documentName", formData.documentName);
      formDataToSend.append("documentType", formData.documentType);
      formDataToSend.append("documentNo", formData.documentNo);
      formDataToSend.append("date", formData.date);
      formDataToSend.append("details", formData.details);
      formDataToSend.append("uploadDocument", formData.uploadDocument);

      // Convert the arrays of IDs into valid format
      if (Array.isArray(formData.circleId)) {
        formData.circleId.forEach((id) =>
          formDataToSend.append("circleId[]", id)
        );
      }
      if (Array.isArray(formData.divisionId)) {
        formData.divisionId.forEach((id) =>
          formDataToSend.append("divisionId[]", id)
        );
      }
      if (Array.isArray(formData.rangeId)) {
        formData.rangeId.forEach((id) =>
          formDataToSend.append("rangeId[]", id)
        );
      }
      if (Array.isArray(formData.vfcId)) {
        formData.vfcId.forEach((id) => formDataToSend.append("vfcId[]", id));
      }

      const response = await axios.post(
        `${Api}/api/govt-order/documents`,
        formDataToSend,
        {
          headers: {
            ...Config.headers, 
          },
        }
      );

      if (response.status === 201) {
        toast.success("Govt Order added successfully");
        setFormData(initialStage);
        setErrors({});
      } else {
        toast.error("Error submitting form");
      }
    } catch (error) {
      console.error("Error submitting form", error);
      toast.error("Error submitting form");
    }
  };

  return (
    <>
      <Preloader loading={loading} />
      <div className="page-header">
        <h3 className="page-title"> Circle </h3>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="!#" onClick={(event) => event.preventDefault()}>
                VFC
              </a>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Circle
            </li>
          </ol>
        </nav>
      </div>

      <Container>
        <div className="col-md-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <p className="card-description">SHG Registration Form</p>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group controlId="circleId">
                      <Form.Label>Select Circle</Form.Label>
                      <div className="input-container">
                        <Form.Control
                          as="select"
                          value={formData.circleId}
                          onChange={handleSelectChange}
                        >
                          <option value="">Select Circle</option>
                          <option value="ALL">ALL</option>
                          {circles.map((circle) => (
                            <option key={circle._id} value={circle._id}>
                              {circle.circleName}
                            </option>
                          ))}
                        </Form.Control>{" "}
                      </div>
                    </Form.Group>
                  </Col>

                  {/* Division Selection */}
                  <Col md={6}>
                    <Form.Group controlId="divisionId">
                      <Form.Label>Select Division</Form.Label>
                      <div className="input-container">
                        <Form.Control
                          as="select"
                          value={formData.divisionId}
                          onChange={handleSelectChange}
                        >
                          <option value="">Select Division</option>
                          <option value="ALL">ALL</option>
                          {divisions.map((division) => (
                            <option key={division._id} value={division._id}>
                              {division.divisionName}
                            </option>
                          ))}
                        </Form.Control>{" "}
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Range Selection */}
                <Row>
                  <Col md={6}>
                    <Form.Group controlId="rangeId">
                      <Form.Label>Select Range</Form.Label>
                      <div className="input-container">
                        <Form.Control
                          as="select"
                          value={formData.rangeId}
                          onChange={handleSelectChange}
                        >
                          <option value="">Select Range</option>
                          <option value="ALL">ALL</option>
                          {ranges.map((range) => (
                            <option key={range._id} value={range._id}>
                              {range.rangeName}
                            </option>
                          ))}
                        </Form.Control>{" "}
                      </div>
                    </Form.Group>
                  </Col>

                  {/* VFC Selection */}
                  <Col md={6}>
                    <Form.Group controlId="vfcId">
                      <Form.Label>Select VFC</Form.Label>
                      <div className="input-container">
                        <Form.Control
                          as="select"
                          value={formData.vfcId}
                          onChange={handleSelectChange}
                        >
                          <option value="">Select VFC</option>
                          <option value="ALL">ALL</option>
                          {vfcs.map((vfc) => (
                            <option key={vfc._id} value={vfc._id}>
                              {vfc.vfcName}
                            </option>
                          ))}
                        </Form.Control>{" "}
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Document Details */}
                <Row>
                  <Col md={6}>
                    <Form.Group controlId="documentName">
                      <Form.Label>Govt Order Name</Form.Label>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          value={formData.documentName}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              documentName: e.target.value,
                            })
                          }
                        />{" "}
                      </div>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId="documentType">
                      <Form.Label>Govt Order Type</Form.Label>
                      <div className="input-container">
                        <Form.Control
                          as="select"
                          value={formData.documentType}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              documentType: e.target.value,
                            })
                          }
                        >
                          <option value="">Select Document Type</option>
                          <option value="renewal">
                            VFC Renewal Certificate
                          </option>
                          <option value="bylaw">VFC Bye Law</option>
                          <option value="audit">VFC Audit Reports</option>
                          <option value="success">Success Story Reports</option>
                        </Form.Control>{" "}
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Other Fields */}
                <Row>
                  <Col md={6}>
                    <Form.Group controlId="documentNo">
                      <Form.Label>Govt Order No</Form.Label>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          value={formData.documentNo}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              documentNo: e.target.value,
                            })
                          }
                        />{" "}
                      </div>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId="date">
                      <Form.Label>Date</Form.Label>
                      <div className="input-container">
                        <Form.Control
                          type="date"
                          value={formData.date}
                          onChange={(e) =>
                            setFormData({ ...formData, date: e.target.value })
                          }
                        />{" "}
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Details and Upload Document */}
                <Row>
                  <Col md={6}>
                    <Form.Group controlId="details">
                      <Form.Label>Details</Form.Label>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          value={formData.details}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              details: e.target.value,
                            })
                          }
                        />{" "}
                      </div>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId="uploadDocument">
                      <Form.Label>Upload Document</Form.Label>
                      <div className="input-container">
                        <Form.Control
                          type="file" // Ensure this is 'file' type
                          onChange={handleChange}
                          isInvalid={!!errors.uploadDocument}
                        />
                      </div>
                      <Form.Control.Feedback type="invalid">
                        {errors.uploadDocument}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Button variant="primary" type="submit">
                  Submit
                </Button>
              </Form>
            </div>
          </div>
        </div>
      </Container>
    </>
  );
};

export default AddGO;
